/**
 * Static layout of the chime set: which pitch hangs where, how long, how thick, on what cord,
 * and where the clapper disc sits so it meets every tube in its mid-height strike zone.
 */
import type { MaterialName, RingSpec, TubeSpec } from '../types';
import { v3 } from '../types';
import { lengthForFrequency } from './geometry';
import { clamp, lerp } from '../util/math';
import { PHYS } from './params';

/**
 * Ring slots run left to right in X (well spread on screen), alternating front/back so no
 * two tubes stack along the camera axis. rank (0 = lowest pitch) -> slot, chosen by an
 * exhaustive search so that every pair of anchors closer than 0.26 m is at least three
 * steps apart in the tuning list: neighbours collide most, and their clash is the point.
 */
const PITCH_SLOT_8 = [4, 0, 3, 7, 2, 1, 5, 6];

/** by pitch rank: the low three are iron, then oxblood, bone, and one brass on top */
const MATERIALS_8: MaterialName[] = ['iron', 'iron', 'iron', 'oxblood', 'oxblood', 'bone', 'bone', 'brass'];

export interface Layout {
  tubes: TubeSpec[];
  /** kg, by tube index */
  masses: number[];
  /** cord from the ring centre to the clapper disc centre */
  clapperCord: number;
  /** highest and lowest fundamental in the set */
  fMin: number;
  fMax: number;
}

function slotPosition(k: number, n: number, ring: RingSpec) {
  const u = n > 1 ? lerp(-0.95, 0.95, k / (n - 1)) : 0;
  const sgn = k % 2 === 0 ? 1 : -1;
  return v3(ring.center.x + ring.rx * u, ring.center.y, ring.center.z + sgn * ring.rz * Math.sqrt(Math.max(0, 1 - u * u)));
}

function rankToSlot(n: number): number[] {
  if (n === 8) return PITCH_SLOT_8.slice();
  // generic fallback: interleave halves so x-neighbours are far apart in pitch
  const out: number[] = new Array(n);
  const half = Math.ceil(n / 2);
  for (let r = 0; r < n; r++) out[r] = r % 2 === 0 ? r / 2 : half + (r - 1) / 2;
  return out;
}

function materialFor(rank: number, n: number): MaterialName {
  if (n === 8) return MATERIALS_8[rank];
  return MATERIALS_8[Math.min(7, Math.floor((rank / n) * 8))];
}

export function buildLayout(tuningHz: number[], ring: RingSpec): Layout {
  const T = PHYS.tube;
  const n = tuningHz.length;
  const order = tuningHz.map((hz, i) => ({ hz, i })).sort((a, b) => a.hz - b.hz);
  const rankOf = new Array<number>(n);
  order.forEach((o, rank) => { rankOf[o.i] = rank; });
  const slotOf = rankToSlot(n);
  const fMin = order[0].hz, fMax = order[n - 1].hz;

  const lengths = tuningHz.map((hz) => clamp(lengthForFrequency(hz, T.lengthK), T.minLength, T.maxLength));
  let lMin = Infinity, lMax = -Infinity;
  for (const l of lengths) { lMin = Math.min(lMin, l); lMax = Math.max(lMax, l); }
  // shorter tubes hang on longer cords so the clapper meets them all near mid-height
  const cords = lengths.map((l) => (lMax > lMin ? lerp(T.minCord, T.maxCord, (lMax - l) / (lMax - lMin)) : T.minCord));
  // floor guard: never let a tube bottom dip below floorY (scale lengths down uniformly)
  const budget = ring.center.y - T.floorY;
  let k = 1;
  for (let i = 0; i < n; i++) k = Math.min(k, (budget - cords[i]) / lengths[i]);
  if (k < 1) for (let i = 0; i < n; i++) lengths[i] = Math.max(T.minLength, lengths[i] * k);

  const tubes: TubeSpec[] = [];
  const masses: number[] = [];
  for (let i = 0; i < n; i++) {
    const rank = rankOf[i];
    const radius = lerp(T.maxRadius, T.minRadius, n > 1 ? rank / (n - 1) : 0);
    tubes.push({
      index: i,
      freqHz: tuningHz[i],
      cord: cords[i],
      length: lengths[i],
      radius,
      anchor: slotPosition(slotOf[rank], n, ring),
      material: materialFor(rank, n),
    });
    masses.push(T.massPerRadiusMetre * radius * lengths[i]);
  }

  // clapper height: the intersection of every tube's strike zone, or the fairest point between
  const [z0, z1] = PHYS.clapper.strikeZone;
  let lo = -Infinity, hi = Infinity;
  for (const t of tubes) {
    const top = ring.center.y - t.cord;
    lo = Math.max(lo, top - z1 * t.length);
    hi = Math.min(hi, top - z0 * t.length);
  }
  const clapperY = 0.5 * (lo + hi);
  return { tubes, masses, clapperCord: ring.center.y - clapperY, fMin, fMax };
}
